// Soulstack Verify integration (@sstk/verify-widget). The API captcha-gates
// publishing actions; this module owns the browser side: it overlays the
// <sstk-verify> challenge widget, the user solves it, and the resulting
// ONE-USE token travels to the API in the X-Captcha-Token header where it is
// redeemed against the Verify service. When the captcha env is not configured
// (development) requestCaptchaToken resolves to '' and flows are unaffected,
// mirroring the API's empty-secret disabled state.

const CAPTCHA_API_URL =
  import.meta.env.VITE_CAPTCHA_API_URL ||
  'https://us-central1-soulstack-prod.cloudfunctions.net';
const CAPTCHA_SITE_KEY = import.meta.env.VITE_CAPTCHA_SITE_KEY || '';

export function captchaEnabled(): boolean {
  return Boolean(CAPTCHA_SITE_KEY);
}

interface SstkVerifiedDetail {
  token: string;
  expiresAt: number;
}

/**
 * Obtain a fresh one-use Verify token by overlaying the challenge widget.
 * Resolves with the token once the user solves it; rejects when they dismiss
 * the overlay (Escape, backdrop or Cancel). Resolves '' immediately when
 * captcha is not configured. Tokens are consumed on redemption, so callers
 * must request a new one for every gated action.
 */
export async function requestCaptchaToken(): Promise<string> {
  if (!captchaEnabled()) return '';
  await import('@sstk/verify-widget');

  return new Promise<string>((resolve, reject) => {
    const overlay = document.createElement('div');
    overlay.dataset.sstkCaptchaOverlay = 'true';
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '200',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0,0,0,0.6)',
    });

    const panel = document.createElement('div');
    Object.assign(panel.style, {
      background: 'var(--ss-panel)',
      border: '1px solid var(--ss-border)',
      borderRadius: '16px',
      boxShadow: '0 30px 80px rgba(0,0,0,0.7)',
      padding: '22px',
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
      width: 'min(360px, calc(100vw - 32px))',
    });

    const title = document.createElement('span');
    title.textContent = 'Quick check before we update your game';
    Object.assign(title.style, { fontSize: '15px', fontWeight: '600' });

    const widget = document.createElement('sstk-verify');
    widget.setAttribute('site-key', CAPTCHA_SITE_KEY);
    widget.setAttribute('api-url', CAPTCHA_API_URL);
    // Follow Studio's own live theme rather than a fixed dark widget, per
    // data-theme in theme.tsx (dark is the brand default; either can be active).
    const studioTheme = document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
    widget.setAttribute('theme', studioTheme);
    widget.setAttribute('auto-load', 'true');
    Object.assign(widget.style, {
      '--sstk-accent': 'var(--ss-accent)',
      '--sstk-accent-contrast': 'var(--ss-accent-ink)',
      '--sstk-bg': 'var(--ss-panel)',
      '--sstk-fg': 'var(--ss-text)',
      '--sstk-border': 'var(--ss-border)',
    });

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'ss-btn ss-btn--ghost';
    cancel.textContent = 'Cancel';

    function cleanup() {
      document.removeEventListener('keydown', onKeyDown, true);
      overlay.remove();
    }
    function dismiss() {
      cleanup();
      reject(new Error('Verification cancelled.'));
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        dismiss();
      }
    }

    widget.addEventListener('sstk-verified', (event) => {
      const detail = (event as CustomEvent<SstkVerifiedDetail>).detail;
      cleanup();
      resolve(detail.token);
    });
    cancel.addEventListener('click', dismiss);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) dismiss();
    });
    document.addEventListener('keydown', onKeyDown, true);

    panel.append(title, widget, cancel);
    overlay.append(panel);
    document.body.append(overlay);
  });
}

/** Header fragment for gated API calls; empty when captcha is disabled. */
export function captchaHeader(token: string): Record<string, string> {
  return token ? { 'X-Captcha-Token': token } : {};
}
